// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid } from '@mui/material';
import { endOfWeek, startOfWeek } from 'date-fns';
import moment from 'moment';

import { FBoxContainer, FHFilterAndSort, FHWeekDatesPicker } from '../components/base';
import { sessionActions } from '../store';
import { FORTA_WHITE } from '../utils/colors';
import PageContainer from '../components/layout/PageContainer';
import { filterQACards, QAFilterOptions } from '../components/layout/qa/dashboard/utils';
import SessionNotesQACard from '../components/layout/qa/dashboard/SessionNotesQACard';
import QAWeeklyOverview from '../components/layout/qa/dashboard/QAWeeklyOverview';
import {
  FilterAndSortMode,
  BTSessionStatus,
  ClinicalRelationships,
  Themes,
  selectedDateForQAWeekly,
} from '../constants';
import SortIcon from '../components/svgs/SortIcon';
import { QA_DASHBOARD_WEEK_DATE_LS_KEY } from './constants';

import type { IFilterOutput, IProvider } from '../utils/types';
import type { RootState } from '../store';

function QADashboard() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();
  const {
    sessionsReadyForQA: sessionsList,
    loading: sessionListLoading,
    reviewSummaryLoading,
    reviewSummary,
  } = useSelector((state: RootState) => state.sessions);

  const persistDateAndUpdateParams = (date: Date) => {
    localStorage.setItem(QA_DASHBOARD_WEEK_DATE_LS_KEY, date.toISOString());
    const dateParams = {
      [QA_DASHBOARD_WEEK_DATE_LS_KEY]: encodeURIComponent(date.toISOString()),
    };
    setSearchParams(dateParams);
  };

  // Pull date from param and localStorage
  // Provide higher priority to date pulled from params
  // If either param or localStorage containes date return it
  // Else fallback to selectedDateForQAWeekly
  const initialDateToSelect = useMemo(() => {
    const paramDate = searchParams.get(QA_DASHBOARD_WEEK_DATE_LS_KEY);
    const persistedDate = localStorage.getItem(QA_DASHBOARD_WEEK_DATE_LS_KEY);
    const dateStr = paramDate || persistedDate; // Order is important here, as higher priority is given to date in params
    const dateVal = dateStr ? new Date(decodeURIComponent(dateStr)) : selectedDateForQAWeekly;
    return dateVal;
  }, []);

  const [calendarDate, setCalendarDate] = useState<{ startDate: Date; endDate: Date }>({
    startDate: startOfWeek(initialDateToSelect),
    endDate: endOfWeek(initialDateToSelect),
  });

  const [weekPickerDate, setWeekPickerDate] = useState<Date>(initialDateToSelect);

  const [filterOutput, setFilterOutput] = useState<IFilterOutput | null>(null);
  const [filteredSessions, setfilteredSessions] = useState<ISessionMinifiedWithClaimHistory[]>([
    ...sessionsList,
  ]);
  const [selectedBT, setSelectedBT] = useState<undefined | null | IProvider>();

  useEffect(() => {
    dispatch(
      sessionActions.getSessionsWithClaimStatus({
        params: {
          startDate: calendarDate.startDate,
          endDate: calendarDate.endDate,
          include_qa_stats: true,
        },
        getAccessTokenSilently,
      }),
    );

    return () => {
      dispatch(sessionActions.updateState({ key: 'list', data: [] }));
      dispatch(sessionActions.updateState({ key: 'filteredList', data: [] }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarDate]);

  useEffect(() => {
    persistDateAndUpdateParams(weekPickerDate);
    dispatch(
      sessionActions.getReviewSummary({
        getAccessTokenSilently,
      }),
    );

    return () => {
      dispatch(sessionActions.updateState({ key: 'reviewSummary', data: [] }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!filterOutput) return;

    if (
      Object.keys(filterOutput).length > 0 &&
      Object.keys(filterOutput).some((key) => filterOutput[key].length > 0)
    )
      setfilteredSessions(filterQACards(filterOutput, filteredSessions));
    // sort options cleared case
    // reseting sort to deafult[by session start date]
    else
      setfilteredSessions(
        sessionsList
          .filter(
            (session) =>
              ((session.status === BTSessionStatus.EditsRequested ||
                session.status === BTSessionStatus.Completed) &&
                !session.clinical_qa_review) === false &&
              (!selectedBT || (selectedBT && session.session_provider?.id === selectedBT.id)),
          )
          .sort(
            (sessionA, sessionB) =>
              moment(sessionA.date_start).unix() - moment(sessionB.date_start).unix(),
          ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOutput]);

  useEffect(() => {
    // filtering BCBA completed reviews and sorting as per session date ended
    // this logic should be moved to slice once each page has it's own slice.
    let finalFilteredandSortedList: ISessionMinifiedWithClaimHistory[] = sessionsList
      .filter(
        (session) =>
          ((session.status === BTSessionStatus.EditsRequested ||
            session.status === BTSessionStatus.Completed) &&
            !session.clinical_qa_review) === false &&
          (!selectedBT || (selectedBT && session.session_provider?.id === selectedBT.id)),
      )
      .sort(
        (sessionA, sessionB) =>
          moment(sessionA.date_start).unix() - moment(sessionB.date_start).unix(),
      );
    if (filterOutput)
      finalFilteredandSortedList = filterQACards(filterOutput, finalFilteredandSortedList);

    setfilteredSessions(finalFilteredandSortedList);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBT, sessionsList]);

  const handleDateSelect = (date: Date, startDate?: Date, endDate?: Date) => {
    setWeekPickerDate(date);
    persistDateAndUpdateParams(date);
    setCalendarDate({
      startDate: startDate ? startDate : startOfWeek(date),
      endDate: endDate ? endDate : endOfWeek(date),
    });
  };

  const updateQaDashboardData = () => {
    dispatch(
      sessionActions.getSessionsWithClaimStatus({
        params: {
          startDate: calendarDate.startDate,
          endDate: calendarDate.endDate,
          include_qa_stats: true,
        },
        getAccessTokenSilently,
      }),
    );
  };

  return (
    <PageContainer
      dataTestId="qaDashboardContainer"
      sx={{
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Grid container spacing={3} height={'100%'}>
        <Grid item xs="auto" height={'100%'}>
          <QAWeeklyOverview
            loading={reviewSummaryLoading}
            reviewSummary={reviewSummary}
            onUpdate={(selected) => setSelectedBT(selected)}
          />
        </Grid>
        <Grid item flex={1} height={'100%'}>
          <FBoxContainer
            colorTheme={Themes.Primary}
            title="Session note details"
            sx={{ mt: 3, height: '100%', overflow: 'hidden', boxShadow: 'none' }}
            headerStyle={{ flexGrow: 'unset', boxShadow: 'none', borderRadius: '4px 4px 0 0' }}
            contentStyle={{ height: '100%', p: 2, pb: 6 }}
            dataTestId="sessionNoteDetailsSection"
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                mb: 2.5,
              }}
            >
              <FHWeekDatesPicker
                data-testid={weekPickerDate}
                value={weekPickerDate}
                disableNavigation={sessionListLoading}
                onChange={(date: Date, startDate: Date, endDate: Date) => {
                  handleDateSelect(date, startDate, endDate);
                }}
                handlerWidth={540}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                mb: 2.5,
                mr: 3.5,
              }}
            >
              <Box id="qa-dashboard-filter-chips"></Box>
              <FHFilterAndSort
                buttonLabel={'Sort'}
                mode={FilterAndSortMode.Sort}
                filterSortOptions={QAFilterOptions}
                disabled={false}
                chipWrapperId="qa-dashboard-filter-chips"
                showResetButton={false}
                onFilterChange={(data) => {
                  setFilterOutput(data);
                }}
                buttonIcon={SortIcon}
                buttonStyles={
                  filterOutput &&
                  Object.keys(filterOutput).length > 0 &&
                  Object.keys(filterOutput).some((key) => filterOutput[key].length > 0)
                    ? {}
                    : {
                        backgroundColor: 'transparent',
                        border: 'none',
                        boxShadow: 'none',
                      }
                }
              />
            </Box>
            <Box
              data-testid="sessionReviewCardsList"
              sx={{
                overflowY: 'auto',
                height: '100%',
                pr: 2,
                '::-webkit-scrollbar-track': {
                  background: FORTA_WHITE,
                },
              }}
            >
              {sessionListLoading && !filteredSessions.length ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <Box key={i} mb={2}>
                    <SessionNotesQACard
                      loading={true}
                      session={{
                        client_id: 0,
                        date_start: moment(),
                        utc_offset_date_start: 0,
                        utc_offset_date_end: 0,
                        status: BTSessionStatus.Started,
                        expected_duration: 0,
                        clinical_relationship: ClinicalRelationships.Prbt,
                        date_provider_signed: null,
                        claim_history: [],
                      }}
                      updateSessions={() => {}}
                    />
                  </Box>
                ))
              ) : (
                <>
                  {filteredSessions?.map((sessionItem, index) => (
                    <Box
                      key={index}
                      sx={{ mb: 2 }}
                      data-testid={`${sessionItem.client?.first_name.replace(
                        /\s/g,
                        '',
                      )}_${sessionItem.client?.last_name.replace(/\s/g, '')}_${
                        sessionItem.status
                      }_sessionReviewCard`}
                    >
                      <SessionNotesQACard
                        loading={sessionListLoading}
                        session={sessionItem}
                        updateSessions={updateQaDashboardData}
                      />
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </FBoxContainer>
        </Grid>
      </Grid>
    </PageContainer>
  );
}

export default QADashboard;
